import { useEffect, useState, type ComponentType } from "react";
import { ProjectConfig, resolveEndpoints, type TourDocument } from "@latelierdanslesnuages/schema";
import {
  AutoRotateToggle,
  ContextMenu,
  FormatNotice,
  NavigationMap,
  SceneTitle,
  TourMenu,
  TourPlayer,
  VariantControllers,
  useTourDocument,
} from "@latelierdanslesnuages/player";
import projectJson from "../project.json";
import { AnnonceScene } from "./AnnonceScene.js";
import { Drapeaux } from "./Drapeaux.js";
import {
  centeredStyle,
  issuesStyle,
  liveBadgeStyle,
  toggleStyle,
} from "./styles.js";

/**
 * ============================================================================
 *  Au 14 — la coquille
 * ----------------------------------------------------------------------------
 *  Tout le code de ce projet tient ici et dans deux modules voisins. Le reste
 *  est de la donnée : un document servi depuis R2, des tuiles adressées par
 *  contenu, un registre qui relie un commit à l'état des médias en ligne.
 *
 *  Le moteur est une dépendance ÉPINGLÉE (`0.1.0`, jamais `^0.1`). Cette visite
 *  ne changera pas parce que le moteur a évolué pour un autre client ; sa
 *  montée de version sera un acte délibéré.
 * ==========================================================================*/

const project = ProjectConfig.parse(projectJson);
const params = new URLSearchParams(window.location.search);

const EDIT_MODE = params.has("edit");

/**
 * `?live` — consulter la visite publiée depuis un poste de développement.
 * Sans ce paramètre, `pnpm dev` lit le document et les assets locaux.
 */
const LIVE_MODE = params.has("live");

const endpoints = resolveEndpoints(project, import.meta.env.DEV && !LIVE_MODE);

type EditorComponent = ComponentType<{ tour: TourDocument; assetBaseUrl: string }>;

/**
 * L'éditeur est chargé À LA DEMANDE.
 *
 * Un import statique le ferait entrer dans le bundle principal, et chaque
 * visiteur téléchargerait des panneaux qu'il n'ouvrira jamais. Un import
 * dynamique en fait un fragment distinct, que seul `?edit` va chercher — c'est
 * ce qui rend vraie DANS LE BUNDLE la promesse « la visite livrée n'embarque
 * rien de l'éditeur », et pas seulement dans l'intention.
 *
 * Écrit à la main plutôt qu'avec `React.lazy` : c'est le même mécanisme d'état
 * asynchrone que partout ailleurs, sans imposer un `Suspense` pour un unique
 * point de chargement.
 */
function useEditorView(enabled: boolean): EditorComponent | null {
  const [view, setView] = useState<EditorComponent | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void import("./EditorView.js").then((module) => {
      // La fonction de mise à jour reçoit un COMPOSANT : sans l'enrober, React
      // l'appellerait comme un calculateur d'état.
      if (!cancelled) setView(() => module.EditorView);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return view;
}

export function App() {
  const state = useTourDocument(endpoints.documentUrl, project.tenantId);
  const EditorView = useEditorView(EDIT_MODE);

  if (state.status === "loading") {
    return <div style={centeredStyle}>Chargement de la visite…</div>;
  }

  if (state.status === "error") {
    return (
      <div style={centeredStyle}>
        <strong style={{ color: "#f88" }}>{state.message}</strong>
        <pre style={{ margin: 0, fontSize: 12, opacity: 0.75, whiteSpace: "pre-wrap" }}>
          {state.issues.join("\n")}
        </pre>
      </div>
    );
  }

  const { tour, issues } = state;

  if (EDIT_MODE) {
    if (!EditorView) return <div style={centeredStyle}>Chargement de l'éditeur…</div>;
    return <EditorView tour={tour} assetBaseUrl={endpoints.assetBaseUrl} />;
  }

  return (
    <TourPlayer tour={tour} assetBaseUrl={endpoints.assetBaseUrl}>
      <TourMenu style={{ position: "absolute", top: 12, left: 12, width: 200 }} />
      <NavigationMap
        assetBaseUrl={endpoints.assetBaseUrl}
        width={240}
        style={{ position: "absolute", bottom: 12, left: 12 }}
      />

      {/* Coin des réglages globaux : ce qui vaut pour toute la visite, par
          opposition aux commutateurs, qui ne valent que pour la scène. */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "flex-end",
        }}
      >
        <Drapeaux />
        <AutoRotateToggle framed={false} />
      </div>

      {/* Les commutateurs — allumer, éteindre — sont des gestes et non des
          réglages : ils se placent sous la main plutôt que dans un coin, et au
          centre parce qu'ils portent sur ce qu'on regarde.

          `only` écarte les axes qui s'affichent en encart de choix. Le moteur
          ignore lesquels : la distinction tient à la forme des données, pas au
          nom des axes. */}
      <VariantControllers
        only="switches"
        style={{
          position: "absolute",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          alignItems: "center",
        }}
      />

      {/* Le lien d'édition n'a de sens qu'en développement : une visite livrée
          n'expose pas de porte d'entrée vers son éditeur. */}
      {import.meta.env.DEV && (
        <a href="?edit" style={toggleStyle}>
          Éditer
        </a>
      )}

      {/* L'annonce de scène est entièrement composée par ce projet : plume,
          disposition, filet. Le moteur ne garde que ce que lui seul peut faire
          — le moment, le nom résolu dans la langue courante, l'assombrissement
          de la scène et l'animation d'entrée.

          Il ne reste donc à régler ici que ce qui relève du CADRE : le corps du
          texte, dont tout le reste dérive en `em`, et la position à l'écran. */}
      <SceneTitle style={{ fontSize: "clamp(27.5px, 5.25vw, 55px)", top: "26%" }}>
        {({ title }) => <AnnonceScene nom={title} />}
      </SceneTitle>
      <ContextMenu />
      <FormatNotice style={{ position: "absolute", bottom: 60, right: 12 }} />

      {LIVE_MODE && (
        <a href="?" style={liveBadgeStyle} title={endpoints.assetBaseUrl}>
          servi par R2
        </a>
      )}

      {/* Les incohérences de références intéressent l'auteur, pas le visiteur. */}
      {import.meta.env.DEV && issues.length > 0 && (
        <pre style={issuesStyle}>{issues.join("\n")}</pre>
      )}
    </TourPlayer>
  );
}
